import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@material-ui/core';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';

const useStyles = makeStyles(() => ({
  banner: {
    minHeight: 38,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '5px 16px',
    background: '#e0f7fa',
    borderBottom: '1px solid #b2ebf2',
    color: '#006064',
  },
  bannerText: {
    flex: 1,
    fontSize: 13,
  },
  bannerButton: {
    textTransform: 'none',
    color: '#006064',
    borderColor: '#80cbc4',
  },
  intro: {
    padding: 12,
    background: '#f2fbfc',
    border: '1px solid #d4f1f3',
    borderRadius: 6,
    marginBottom: 12,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 2px',
  },
  rowMain: {
    minWidth: 0,
    flex: 1,
    display: 'block',
    textAlign: 'left',
    borderRadius: 4,
    padding: '4px 6px',
  },
  areaName: {
    fontWeight: 600,
    fontSize: 14,
  },
  source: {
    color: '#607d8b',
    fontSize: 12,
    marginTop: 2,
  },
  moveButton: {
    flexShrink: 0,
    textTransform: 'none',
  },
  error: {
    color: '#b71c1c',
    background: '#ffebee',
    borderRadius: 4,
    padding: '8px 10px',
    marginTop: 8,
    fontSize: 13,
  },
  dirtyWarning: {
    color: '#8d5b00',
    background: '#fff8e1',
    borderRadius: 4,
    padding: '8px 10px',
    marginBottom: 8,
    fontSize: 13,
  },
}));

const formatSquareFeet = value => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? `${Math.round(number).toLocaleString()} SF` : '';
};

const propertyLabel = shadow => (
  shadow?.sourceJobsite?.name || shadow?.sourceJobsite?.address || 'Nearby property'
);

const NearbyAreaShadowsBanner = ({ controller, dirty }) => {
  const classes = useStyles();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const {
    neighbors = [],
    shadows = [],
    rootShadows = [],
    visible = true,
    loading,
    moving,
    error,
    selectedShadow,
    selectShadow,
    focusShadow,
    moveAreaHere,
  } = controller || {};

  useEffect(() => {
    if (selectedShadow) setReviewOpen(true);
  }, [selectedShadow]);

  const childCounts = useMemo(() => {
    const counts = {};
    shadows.forEach(shadow => {
      const parentId = shadow.parentAreaId?._id || shadow.parentAreaId;
      if (parentId) counts[parentId] = (counts[parentId] || 0) + 1;
    });
    return counts;
  }, [shadows]);

  if (!loading && rootShadows.length === 0) return null;

  const closeReview = () => {
    setReviewOpen(false);
    setPendingMove(null);
    selectShadow?.(null);
  };

  const reviewShadow = shadow => {
    selectShadow?.(shadow);
    focusShadow?.(shadow);
  };

  const confirmMove = async () => {
    if (!pendingMove || dirty) return;
    const result = await moveAreaHere?.(pendingMove);
    if (result) {
      setPendingMove(null);
      setReviewOpen(false);
    }
  };

  return (
    <>
      <div className={classes.banner}>
        <VisibilityOutlinedIcon style={{ fontSize: 19 }} />
        <Typography className={classes.bannerText}>
          {loading
            ? 'Looking for nearby same-customer areas…'
            : `${rootShadows.length} nearby ${rootShadows.length === 1 ? 'area' : 'areas'} from ${neighbors.length} ${neighbors.length === 1 ? 'property' : 'properties'} ${visible ? 'shown as dashed shadows' : 'available (map layer hidden)'}`}
        </Typography>
        {!loading && (
          <Button
            size="small"
            variant="outlined"
            className={classes.bannerButton}
            onClick={() => setReviewOpen(true)}
          >
            Review ownership
          </Button>
        )}
      </div>

      <Dialog open={reviewOpen} onClose={moving ? undefined : closeReview} maxWidth="sm" fullWidth>
        <DialogTitle>Nearby property areas</DialogTitle>
        <DialogContent>
          <Box className={classes.intro}>
            <Typography variant="body2">
              Dashed teal shapes belong to another nearby property for this same customer. They are read-only here until you deliberately move one to this property.
            </Typography>
          </Box>

          {dirty && (
            <div className={classes.dirtyWarning}>
              Save the current takeoff before moving an area so your unsaved work is not replaced.
            </div>
          )}
          {error && <div className={classes.error}>{error}</div>}

          {rootShadows.map((shadow, index) => {
            const id = shadow._id || shadow.id;
            const childCount = childCounts[id] || 0;
            const areaSize = formatSquareFeet(shadow.area);
            return (
              <React.Fragment key={id}>
                {index > 0 && <Divider />}
                <div className={classes.row}>
                  <ButtonBase className={classes.rowMain} onClick={() => reviewShadow(shadow)}>
                    <Typography className={classes.areaName}>{shadow.name || 'Unnamed area'}</Typography>
                    <Typography className={classes.source}>
                      {propertyLabel(shadow)} · {Number(shadow.distanceMiles || 0).toFixed(2)} mi away
                      {areaSize ? ` · ${areaSize}` : ''}
                      {childCount ? ` · includes ${childCount} exclusion ${childCount === 1 ? 'area' : 'areas'}` : ''}
                    </Typography>
                  </ButtonBase>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    className={classes.moveButton}
                    startIcon={<SwapHorizIcon />}
                    disabled={dirty || moving}
                    onClick={() => setPendingMove(shadow)}
                  >
                    Move here
                  </Button>
                </div>
              </React.Fragment>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReview} disabled={moving}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingMove)} onClose={moving ? undefined : () => setPendingMove(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Move area to this property?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{pendingMove?.name || 'This area'}</strong> will be copied into the current takeoff and removed from the latest takeoff for <strong>{propertyLabel(pendingMove)}</strong>. Older takeoff snapshots will stay unchanged.
          </Typography>
          {childCounts[pendingMove?._id] > 0 && (
            <Typography variant="body2" style={{ marginTop: 10 }}>
              Its {childCounts[pendingMove?._id]} linked exclusion {childCounts[pendingMove?._id] === 1 ? 'area' : 'areas'} will move with it.
            </Typography>
          )}
          {error && <div className={classes.error}>{error}</div>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingMove(null)} disabled={moving}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={confirmMove}
            disabled={moving || dirty}
            startIcon={moving ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}
          >
            {moving ? 'Moving…' : 'Move area'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NearbyAreaShadowsBanner;
